/**
 * Phase 76 — survey-player runtime evaluation of compute/write logic
 * blocks for UX preview (e.g. piping). Shares the AST + evaluator
 * implementation with the API via `@diy-platform/logic-eval`.
 *
 * Source-of-truth note: the BE recompute at session completion is the
 * authoritative variable producer (persisted to `session.variables`).
 * Player evaluation is best-effort and is overwritten by the BE.
 *
 * Feature-flag note: this module evaluates any compute/write rules the
 * BE returns in the survey payload. The BE recompute at submit is
 * separately FF-gated, so a flag-off survey simply has nothing to
 * evaluate (the BE doesn't write variables back). No FF lookup needed
 * client-side.
 */
import {
  collectQuestionRefs,
  collectVariableRefs,
  evaluate,
  type EvalContext,
  type EvalValue,
  type Expression,
} from '@diy-platform/logic-eval';

import type {
  ComputeAction,
  LogicRule,
  WriteAction,
} from '@/types';

/** Answer values flat per question code; matches BE evaluator shape. */
export type AnswerMap = Readonly<Record<string, EvalValue | EvalValue[]>>;

interface PreparedBlock {
  rule: LogicRule;
  action: ComputeAction | WriteAction;
  expression: Expression;
  questionRefs: Set<string>;
  variableRefs: Set<string>;
}

/**
 * Filter to active compute/write blocks with valid action shape. Matches
 * the BE's active-only filter (review fix P1b).
 */
export function selectBlocks(rules: LogicRule[]): PreparedBlock[] {
  const out: PreparedBlock[] = [];
  for (const rule of rules) {
    if (!rule.isActive) continue;
    if (rule.type !== 'compute' && rule.type !== 'write') continue;
    const action = rule.action as ComputeAction | WriteAction;
    if (!action || (action.type !== 'compute' && action.type !== 'write')) {
      continue;
    }
    if (!action.expression || typeof action.expression !== 'object') continue;
    const questionRefs = new Set<string>();
    const variableRefs = new Set<string>();
    collectQuestionRefs(action.expression, questionRefs);
    collectVariableRefs(action.expression, variableRefs);
    out.push({
      rule,
      action,
      expression: action.expression,
      questionRefs,
      variableRefs,
    });
  }
  return out;
}

/**
 * Topological order: dependents AFTER dependencies. Skips blocks that
 * participate in a cycle (drops them out of the result; player just
 * won't evaluate cyclic ones). BE publish-gate is the authoritative
 * cycle-rejecter.
 */
export function topoOrder(blocks: PreparedBlock[]): PreparedBlock[] {
  const writers = new Map<string, PreparedBlock[]>();
  for (const b of blocks) {
    if (!writers.has(b.action.variable)) writers.set(b.action.variable, []);
    writers.get(b.action.variable)!.push(b);
  }

  const dependsOn = new Map<string, Set<string>>();
  const dependedBy = new Map<string, Set<string>>();
  for (const b of blocks) {
    dependsOn.set(b.rule.id, new Set());
    dependedBy.set(b.rule.id, new Set());
  }
  for (const b of blocks) {
    for (const v of b.variableRefs) {
      for (const dep of writers.get(v) ?? []) {
        if (dep.rule.id === b.rule.id) continue;
        dependsOn.get(b.rule.id)!.add(dep.rule.id);
        dependedBy.get(dep.rule.id)!.add(b.rule.id);
      }
    }
  }

  const byId = new Map(blocks.map((b) => [b.rule.id, b]));
  const cmp = (a: PreparedBlock, b: PreparedBlock) =>
    a.rule.priority - b.rule.priority || a.rule.id.localeCompare(b.rule.id);
  const ready: PreparedBlock[] = blocks
    .filter((b) => (dependsOn.get(b.rule.id)?.size ?? 0) === 0)
    .sort(cmp);
  const ordered: PreparedBlock[] = [];

  while (ready.length > 0) {
    const n = ready.shift()!;
    ordered.push(n);
    for (const downId of dependedBy.get(n.rule.id) ?? []) {
      const set = dependsOn.get(downId)!;
      set.delete(n.rule.id);
      if (set.size === 0) {
        const downBlock = byId.get(downId);
        if (downBlock) {
          let inserted = false;
          for (let i = 0; i < ready.length; i++) {
            if (cmp(ready[i], downBlock) > 0) {
              ready.splice(i, 0, downBlock);
              inserted = true;
              break;
            }
          }
          if (!inserted) ready.push(downBlock);
        }
      }
    }
  }
  return ordered;
}

export interface EvalAllResult {
  variables: Record<string, EvalValue>;
  /** Block ids that failed (e.g. missing answer, type mismatch). */
  errors: { ruleId: string; variable: string; error: string }[];
}

/**
 * Evaluate every block in topological order. Per-block failures are
 * recorded but do not abort subsequent evaluations — mirrors the BE
 * recompute behavior.
 *
 * IMPORTANT: starts from an empty variable map every call. The BE's
 * `LogicBlocksRecomputeService.recomputeForSession()` also starts from
 * `{}` — seeding from a prior result would let a stale value linger
 * when the block that produced it later starts failing (e.g. its
 * referenced answer got cleared). Player drift from BE in that scenario
 * is observable and bad.
 */
export function evaluateAllBlocks(
  rules: LogicRule[],
  answers: AnswerMap,
  now?: Date,
): EvalAllResult {
  const blocks = topoOrder(selectBlocks(rules));
  const variables: Record<string, EvalValue> = {};
  const errors: EvalAllResult['errors'] = [];

  const ctx: EvalContext = {
    answers,
    variables,
    now: now ?? new Date(),
  };

  for (const block of blocks) {
    const result = evaluate(block.expression, ctx);
    if (!result.ok) {
      errors.push({
        ruleId: block.rule.id,
        variable: block.action.variable,
        error: `${result.error}: ${result.message}`,
      });
      continue;
    }
    variables[block.action.variable] = result.value;
  }

  return { variables, errors };
}

/**
 * Re-evaluate compute/write blocks after an answer change.
 *
 * Returns the full new variable map (not just changed keys) so callers
 * can replace the whole map atomically. v0.1 always runs a full topo
 * evaluation when any blocks exist — correct under all dependency
 * shapes (direct + transitive variable references), cheap for the
 * block counts we expect (≤ a few dozen per survey), and avoids the
 * tricky partial-recompute bookkeeping that a per-question dependency
 * graph would require.
 *
 * The `changedQuestionCode` parameter is accepted to keep the call
 * site narrative explicit (and to leave room for a partial-recompute
 * optimization in v0.2 without changing the interface), but is not
 * inspected in v0.1.
 *
 * Does NOT accept a base-variables seed: each call returns the full
 * fresh variable state. Callers must replace, not merge — otherwise a
 * block that starts failing after previously succeeding would leave
 * its stale value in the caller's state (and disagree with the BE
 * recompute at session completion, which always starts from `{}`).
 */
export function recomputeForAnswerChange(
  _changedQuestionCode: string,
  rules: LogicRule[],
  answers: AnswerMap,
): EvalAllResult {
  const blocks = selectBlocks(rules);
  if (blocks.length === 0) {
    return { variables: {}, errors: [] };
  }
  return evaluateAllBlocks(rules, answers);
}
